import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { AppState } from '../store/store';
import { customListRecordApi, customListSchemaApi } from '../apis';
import {
  InternalApiCustomListRecord,
  InternalApiCustomListRecordStatus,
  InternalApiCustomListSchemaField,
  InternalApiCustomListSchemaFieldType,
} from '../api-internal';
import { useNavigate, useParams } from 'react-router';
import './CustomList.scss';
import { SalvButton, SalvSearchField, SalvTable, SalvTablePaginationType, Size } from '@salvapp/ui-components';
import { faDownload, faGear, faPlus } from '@fortawesome/pro-solid-svg-icons';
import { Attribute, ObjectField } from '../common/attributes/types';
import Has from '../auth/Has';
import { CAN_ADD_CUSTOM_LIST_RECORD } from '../auth/permissions';
import { formatDate } from '../common/datetime/FormattedDateTime';
import { downloadDocument, filenameFromContentDisposition } from '../common/document/helper';
import { isFeatureEnabled, ORGANISATION_FEATURE } from '../organisation/feature/helper';
import { useMe } from '../organisation/useMe';
import useCustomLists from './useCustomLists';
import { useArchivedPage } from '../common/useArchivedPage';
import SalvPage from '../common/SalvPage';
import { getCustomListRecordPath, InternalNavigation } from '../navigation/helper';
import { createColumnHelper, PaginationState } from '@tanstack/react-table';
import { CustomListRecordStatusLabel } from './record/CustomListRecordStatusLabel';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

const columnHelper = createColumnHelper<InternalApiCustomListRecord>();
const PAGE_SIZE = 20;

export const CustomList = () => {
  const navigate = useNavigate();
  const customListId = useParams().customListId!;
  const [showingArchived, navigateToArchivedPage] = useArchivedPage(InternalNavigation.customLists);

  const customList = useCustomLists().find((list) => list.id === customListId);
  const customListsLoaded = useSelector((state: AppState) => state.customLists.customLists !== null);
  const { features } = useMe();
  const showExportAndArchive = isFeatureEnabled(ORGANISATION_FEATURE.CUSTOM_LIST_RECORD_EXPORT_AND_ARCHIVE, features);

  useEffect(() => {
    if (customListsLoaded && !customList) {
      navigate(InternalNavigation.customLists);
    }
  }, [customListsLoaded, customList, navigate]);

  const [searchTerm, setSearchTerm] = useState<string>('');
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: PAGE_SIZE });

  const handleNavigateToArchivedPage = () => {
    setPagination({ pageIndex: 0, pageSize: PAGE_SIZE });
    setSearchTerm('');
    navigateToArchivedPage();
  };

  const { data: schemaFields } = useQuery({
    queryKey: ['customListSchema', customListId],
    queryFn: () => customListSchemaApi.getCustomListSchema({ customListId }).then((result) => result.schemaFields),
    enabled: !!customList,
  });

  const { data: recordsPage } = useQuery({
    queryKey: ['customListRecords', customListId, pagination.pageIndex, searchTerm, showingArchived],
    queryFn: () =>
      customListRecordApi.getRecords({
        customListId,
        page: pagination.pageIndex,
        size: PAGE_SIZE,
        query: searchTerm || undefined,
        searchArchived: showingArchived,
      }),
    enabled: !!customList,
    placeholderData: keepPreviousData,
  });

  const renderMultiChildrenValues = (
    attributeList?: ObjectField[],
    schemaFields?: InternalApiCustomListSchemaField[]
  ) => {
    if (!attributeList || attributeList.length === 0 || !attributeList.length) {
      return <>--</>;
    }
    if (attributeList.length === 1) {
      return renderChildrenValues([attributeList[0]], schemaFields, ' ');
    }
    return (
      <>
        {renderChildrenValues([attributeList[0]], schemaFields, ' ')}
        <span> and more </span>
      </>
    );
  };

  const renderChildrenValues = (
    attributeList?: ObjectField[],
    schemaFields?: InternalApiCustomListSchemaField[],
    separator = ', '
  ) => {
    const values = attributeList
      ?.filter((attribute) => !!attribute)
      ?.map((attribute) => {
        return schemaFields
          ?.filter((schema) => !!schema && attribute[schema.fieldName])
          ?.map((schema) => attribute[schema.fieldName]);
      })
      .flat();

    return <span>{values?.join(separator) || '--'}</span>;
  };

  const handleMultiValues = (record: InternalApiCustomListRecord, field: InternalApiCustomListSchemaField) => {
    switch (field.type) {
      case InternalApiCustomListSchemaFieldType.OBJECT:
        return renderMultiChildrenValues(
          getFieldValue<ObjectField[]>(record, field),
          schemaFields?.filter((f: InternalApiCustomListSchemaField) => f.parentId === field.id)
        );
      default:
        return (
          <span key={field.id}>
            {/*
              Make sure that the received value is an array otherwise everything blows up.
              Fallback to single value presentation
            */}
            {typeof getFieldValue(record, field) === 'object'
              ? getFieldValue<string[]>(record, field)?.join(', ') || '--'
              : getFieldValue<string>(record, field) || '--'}
          </span>
        );
    }
  };

  const handleSingleValues = (record: InternalApiCustomListRecord, field: InternalApiCustomListSchemaField) => {
    switch (field.type) {
      case InternalApiCustomListSchemaFieldType.OBJECT: {
        const objectFieldValue = getFieldValue<ObjectField>(record, field);
        return renderChildrenValues(
          objectFieldValue ? [objectFieldValue] : undefined,
          schemaFields?.filter((f) => f.parentId === field.id)
        );
      }
      default:
        return <span key={field.id}>{getFieldValue<string>(record, field)}</span>;
    }
  };

  const getFieldValue = <T extends Attribute>(
    record: InternalApiCustomListRecord,
    field: InternalApiCustomListSchemaField
  ) => {
    if (!record.attributes) {
      return undefined;
    }
    return record.attributes[field.fieldName] as T;
  };

  const getRecordValue = (record: InternalApiCustomListRecord, field: InternalApiCustomListSchemaField) => {
    return field.isMulti ? handleMultiValues(record, field) : handleSingleValues(record, field);
  };

  const visibleSchemaFields = useMemo(
    () => schemaFields?.filter((field) => !field.parentId && field.visibleInList) ?? [],
    [schemaFields]
  );

  const columns = useMemo(
    () => [
      ...visibleSchemaFields.map((schemaField) =>
        columnHelper.display({
          id: schemaField.id,
          header: schemaField.displayName,
          cell: ({ row }) => getRecordValue(row.original, schemaField),
        })
      ),
      columnHelper.display({
        id: 'lastUpdatedBy',
        header: 'Last updated by',
        cell: ({ row }) => (
          <div className='pt-0 pb-0'>
            <span className='text-xs text-muted'>{row.original?.lastUpdatedBy?.name ?? 'System'}</span>
            <br />
            <span className='text-xs text-muted'>
              {row.original.lastUpdatedTime && formatDate(row.original.lastUpdatedTime, true)}
            </span>
            {row.original.status === InternalApiCustomListRecordStatus.DRAFT && (
              <CustomListRecordStatusLabel status={row.original.status} />
            )}
          </div>
        ),
      }),
    ],
    [visibleSchemaFields, schemaFields]
  );

  const handleDownloadRecordIds = async () => {
    const result = await customListRecordApi.downloadRecords({ customListId });
    const filename = filenameFromContentDisposition(
      result.headers,
      `custom-list-record-ids-${customList?.code ?? customListId}.csv`
    );
    downloadDocument(result.body, filename);
  };

  const handleSearch = (query: string) => {
    setSearchTerm(query);
    setPagination({ pageIndex: 0, pageSize: PAGE_SIZE });
  };

  return (
    <SalvPage
      title={`${showingArchived ? 'Archived records for' : ''} ${customList?.name}`}
      subTitle={<span className='text-secondary me-4'>List ID: {customList?.id}</span>}
      breadcrumb={{
        title: showingArchived ? 'Custom List' : 'Custom Lists',
        to: showingArchived ? './' : '/custom-lists',
      }}
      actions={
        !showingArchived && (
          <>
            <SalvButton
              label={'List settings'}
              icon={faGear}
              variant='muted'
              size={Size.SMALL}
              onClick={() => navigate(`/custom-lists/${customList?.id}/settings`)}
            />
            <SalvButton
              label={'View archived records'}
              variant='muted'
              size={Size.SMALL}
              onClick={handleNavigateToArchivedPage}
            />
            {showExportAndArchive && (
              <SalvButton
                label={'Download records'}
                icon={faDownload}
                variant='muted'
                size={Size.SMALL}
                onClick={handleDownloadRecordIds}
              />
            )}
          </>
        )
      }
      className='custom-list'
    >
      <SalvTable
        data={recordsPage?.content ?? []}
        columns={columns}
        hover
        emptyMessage='Nothing to show here, yet.'
        rowClick={(row) => navigate(getCustomListRecordPath(customListId, row.original.id))}
        actions={
          <>
            <SalvSearchField
              value={searchTerm}
              onChange={handleSearch}
              placeholder='Search'
              size={Size.SMALL}
              ariaLabel='Search records'
            />
            {customList?.canEdit && !showingArchived && (
              <Has allPermissions={CAN_ADD_CUSTOM_LIST_RECORD}>
                <SalvButton
                  label='New record'
                  icon={faPlus}
                  variant='primary'
                  size={Size.SMALL}
                  onClick={() => navigate(`/custom-lists/${customListId}/records`)}
                />
              </Has>
            )}
          </>
        }
        pagination={{
          type: SalvTablePaginationType.PAGINATION,
          total: recordsPage?.total ?? 0,
          state: pagination,
          setPagination,
        }}
      />
    </SalvPage>
  );
};

export default CustomList;
